import React, { useEffect, useMemo, useState } from "react";
import MessengerLayout        from "../../../layout/MessengerLayout";
import PerformanceFilters     from "./filters/PerformanceFilters";
import PerformanceKpis        from "./kpi/PerformanceKpis";
import CycleActionsBar        from "./cycles/CycleActionsBar";
import PerformanceReviewsGrid from "./grid/PerformanceReviewsGrid";
import ReviewDrawer           from "./drawer/ReviewDrawer";
import CalibrationPanel       from "./calibrations/CalibrationPanel";
import NineBoxMatrix          from "./ninebox/NineBoxMatrix";
import ExceptionsPanel        from "./exceptions/ExceptionsPanel";
import ProfilesTab            from "./tabs/ProfilesTab";
import CyclesTab              from "./tabs/CyclesTab";
import GoalsTab               from "./tabs/GoalsTab";
import PerformanceReportsTab  from "./tabs/PerformanceReportsTab";
import PIPTab                 from "../../performance/PIPTab";
import CareerTab              from "../../performance/CareerTab";
import TransferTab            from "../../performance/TransferTab";
import SalaryCalibrationTab   from "../../performance/SalaryCalibrationTab";
import { toast }              from "../../ui/Toast";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" });

const TABS = [
  { key: "overview", label: "Overview"  },
  { key: "reviews",  label: "Reviews"   },
  { key: "profiles", label: "Profiles"  },
  { key: "cycles",   label: "Cycles"    },
  { key: "goals",    label: "Goals"     },
  { key: "pip",      label: "PIP"       },
  { key: "career",   label: "Career"    },
  { key: "transfer", label: "Transfers" },
  { key: "salary",   label: "Salary Review" },
  { key: "reports",  label: "Reports"   },
];

export default function AdminPerformancePage() {
  const [tab,  setTab]  = useState("overview");

  const [filters, setFilters] = useState({
    cycleId: "", location: "ALL", department: "ALL", stage: "ALL",
  });

  const [cycle,      setCycle]      = useState(null);
  const [rows,       setRows]       = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [kpis,       setKpis]       = useState(null);
  const [selected,   setSelected]   = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);

  useEffect(() => {
    if (tab !== "overview" && tab !== "reviews") return;

    const run = () => {
      setLoading(true); setError(null);
      Promise.all([
        fetch(`${API}/api/admin/performance/cycle`,      { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API}/api/admin/performance/kpis`,       { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API}/api/admin/performance/reviews`,    { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API}/api/admin/performance/exceptions`, { headers: authHeaders() }).then(r => r.json()),
      ])
        .then(([cycleRes, kpiRes, reviewRes, excRes]) => {
          const validCycle = cycleRes?.id ? cycleRes : null;
          setCycle(validCycle);
          setFilters(f => ({ ...f, cycleId: validCycle?.id || "" }));
          setKpis(kpiRes);
          setRows(Array.isArray(reviewRes) ? reviewRes : reviewRes?.rows || reviewRes?.reviews || reviewRes?.data || []);
          setExceptions(excRes || []);
        })
        .catch(e => setError(e.message))
        .finally(() => setLoading(false));
    };

    run();
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["goal_create", "self_review", "manager_review"].includes(t)) {
        run();
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => window.removeEventListener("kira_action_complete", handleKiraAction);
  }, [tab]);

  const derived = useMemo(() => {
    if (!Array.isArray(rows)) return { filtered: [], completion: 0, avgRating: "0.00" };
    const filtered = rows.filter(r => {
      if (filters.location   !== "ALL" && r.location !== filters.location)   return false;
      if (filters.department !== "ALL" && r.dept     !== filters.department) return false;
      if (filters.stage      !== "ALL" && r.stage    !== filters.stage)      return false;
      return true;
    });
    const completion = filtered.length === 0 ? 0
      : Math.round((filtered.filter(r => r.stage === "Finalize").length / filtered.length) * 100);
    const avgRating = filtered.length === 0 ? "0.00"
      : (filtered.reduce((s, r) => s + (r.rating || 0), 0) / filtered.length).toFixed(2);
    return { filtered, completion, avgRating };
  }, [rows, filters]);

  const post = (url, body = {}) =>
    fetch(url, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) }).then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    });

  const startCycle  = () =>
    post(`${API}/api/admin/performance/cycle/start`)
      .then(c => { setCycle(c); toast.success("Review cycle started"); })
      .catch(() => toast.error("Failed to start cycle"));

  const lockRatings = () =>
    cycle?.id && post(`${API}/api/admin/performance/cycle/${cycle.id}/lock`)
      .then(c => { setCycle(c); toast.success("Ratings locked"); })
      .catch(() => toast.error("Failed to lock ratings"));

  const closeCycle  = () =>
    cycle?.id && post(`${API}/api/admin/performance/cycle/${cycle.id}/close`)
      .then(c => { setCycle(c); toast.success("Review cycle closed"); })
      .catch(() => toast.error("Failed to close cycle"));

  const bulkNudge   = stage =>
    post(`${API}/api/admin/performance/bulk-nudge`, { stage })
      .then(() => toast.success("Reminders sent to team"))
      .catch(() => toast.error("Failed to send reminders"));

  const moveStage = (id, stage) =>
    post(`${API}/api/admin/performance/review/${id}/stage`, { stage })
      .then(() => {
        setRows(prev => prev.map(r => r.id === id ? { ...r, stage } : r));
        toast.success("Stage updated");
      })
      .catch(() => toast.error("Failed to update stage"));

  const setRating = (id, rating) =>
    post(`${API}/api/admin/performance/review/${id}/rating`, { rating })
      .then(() => {
        setRows(prev => prev.map(r => r.id === id ? { ...r, rating } : r));
        toast.success("Rating saved");
      })
      .catch(() => toast.error("Failed to save rating"));

  const clearException = (id, code) =>
    post(`${API}/api/admin/performance/exceptions/${id}/clear?code=${code}`)
      .then(() => {
        setExceptions(prev => prev.filter(e => e.id !== id));
        toast.success("Exception cleared");
      })
      .catch(() => toast.error("Failed to clear exception"));

  const kpisData = {
    ...kpis,
    total:      derived.filtered.length,
    completion: derived.completion,
    avgRating:  derived.avgRating,
  };

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Performance</h1>
          <p className="text-white/40 text-sm mt-0.5">Manage evaluation profiles, cycles, reviews and calibration</p>
        </div>

        {/* Tab nav */}
        <div className="flex gap-1 flex-wrap border-b border-white/5 pb-1">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`text-xs px-4 py-2 rounded-t-lg border-b-2 transition-colors ${
                tab === t.key ? "border-[#00ff90] text-[#00ff90]" : "border-transparent text-white/40 hover:text-white/70"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview tab */}
        {tab === "overview" && (
          <>
            <CycleActionsBar cycle={cycle} onStart={startCycle} onBulkNudge={bulkNudge} onLock={lockRatings} onClose={closeCycle} />
            {cycle && (
              <PerformanceFilters filters={filters} setFilters={setFilters} cycle={cycle} setCycle={setCycle} />
            )}
            <PerformanceKpis loading={loading} kpis={kpisData} />
            {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <div className="xl:col-span-2 space-y-5">
                <PerformanceReviewsGrid rows={derived.filtered.slice(0, 10)} onOpen={setSelected} />
              </div>
              <div className="space-y-5">
                <ExceptionsPanel rows={exceptions} onClear={clearException} />
                <CalibrationPanel rows={derived.filtered} onMoveStage={moveStage} />
              </div>
            </div>
            <NineBoxMatrix rows={derived.filtered} />
          </>
        )}

        {/* Reviews tab — full grid + calibration */}
        {tab === "reviews" && (
          <>
            {cycle && (
              <PerformanceFilters filters={filters} setFilters={setFilters} cycle={cycle} setCycle={setCycle} />
            )}
            {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}
            <PerformanceReviewsGrid rows={derived.filtered} onOpen={setSelected} />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <CalibrationPanel rows={derived.filtered} onMoveStage={moveStage} />
              <NineBoxMatrix rows={derived.filtered} />
            </div>
          </>
        )}

        {tab === "profiles" && <ProfilesTab />}
        {tab === "cycles"   && <CyclesTab />}
        {tab === "goals"    && <GoalsTab />}
        {tab === "pip"      && <PIPTab />}
        {tab === "career"   && <CareerTab />}
        {tab === "transfer" && <TransferTab />}
        {tab === "salary"   && <SalaryCalibrationTab />}
        {tab === "reports"  && <PerformanceReportsTab />}

      </div>

      <ReviewDrawer
        open={!!selected}
        row={selected}
        onClose={() => setSelected(null)}
        onSetRating={setRating}
        onMoveStage={moveStage}
      />
    </MessengerLayout>
  );
}